import React from 'react';

import { Box, Col, getCSSVariable, Row, Text } from '@opendoor/bricks-next';
import { IconCircleCheckFilled, IconCircleXFilled, IconMinus } from '@tabler/icons-react';
import type { EntryComponent } from 'cms/entries/entries';
import type { ICompareTable, ICompareTableRow } from 'declarations/contentful';

const SENTIMENT_LABELS: Record<string, string> = {
  positive: 'Advantage',
  negative: 'Disadvantage',
  neutral: 'Neutral',
};

const SentimentIcon: React.FC<{ sentiment: string }> = ({ sentiment }) => {
  let icon;
  if (sentiment === 'positive') {
    icon = (
      <IconCircleCheckFilled
        size={18}
        color={getCSSVariable('$contentStateSuccessDefault', 'color')}
        aria-hidden="true"
      />
    );
  } else if (sentiment === 'negative') {
    icon = (
      <IconCircleXFilled
        size={18}
        color={getCSSVariable('$contentStateErrorDefault', 'color')}
        aria-hidden="true"
      />
    );
  } else {
    icon = (
      <IconMinus size={18} color={getCSSVariable('$contentTertiary', 'color')} aria-hidden="true" />
    );
  }

  return (
    <Box accessibilityLabel={SENTIMENT_LABELS[sentiment] || sentiment} flexShrink={0}>
      {icon}
    </Box>
  );
};

const MobileCards: React.FC<{ rows: ICompareTableRow[]; competitorLabel: string }> = ({
  rows,
  competitorLabel,
}) => (
  <Col gap="$4x">
    {rows.map((row) => (
      <Col
        key={row.fields.category}
        backgroundColor="$backgroundPrimary"
        borderRadius="$6x"
        p="$10x"
        gap="$6x"
        $platform-web={{
          boxShadow: '0 1px 2px rgba(20, 35, 58, 0.04), 0 4px 16px rgba(20, 35, 58, 0.04)',
        }}
      >
        <Box pb="$4x" borderBottomWidth={1} borderBottomColor="$borderSecondary">
          <Text tag="span" typography="$labelSmall" color="$contentTertiary" fontWeight="$medium">
            {row.fields.category}
          </Text>
        </Box>
        <Col gap="$2x">
          <Text tag="span" typography="$labelSmall" color="$contentTertiary">
            {competitorLabel}
          </Text>
          <Row gap="$4x" alignItems="flex-start">
            <SentimentIcon sentiment={row.fields.competitorSentiment} />
            <Text tag="span" typography="$bodySmall" color="$contentSecondary">
              {row.fields.competitorText}
            </Text>
          </Row>
        </Col>
        <Col gap="$2x">
          <Text tag="span" typography="$labelSmall" color="$contentAccentBrand">
            Opendoor
          </Text>
          <Row gap="$4x" alignItems="flex-start">
            <SentimentIcon sentiment={row.fields.opendoorSentiment} />
            <Text tag="span" typography="$bodySmall" color="$contentPrimary">
              {row.fields.opendoorText}
            </Text>
          </Row>
        </Col>
      </Col>
    ))}
  </Col>
);

const DesktopGrid: React.FC<{
  rows: ICompareTableRow[];
  competitorLabel: string;
  opendoorLabel: string;
}> = ({ rows, competitorLabel, opendoorLabel }) => (
  <Box
    backgroundColor="$backgroundPrimary"
    borderRadius="$8x"
    accessibilityRole="none"
    // BN accessibilityRole doesn't include "grid"; set role directly for screen readers
    {...({ role: 'grid' } as any)}
    $platform-web={{
      display: 'grid',
      gridTemplateColumns: '20% 40% 40%',
      overflow: 'hidden',
      boxShadow: '0 1px 2px rgba(20, 35, 58, 0.04), 0 4px 16px rgba(20, 35, 58, 0.04)',
    }}
  >
    {/* Header row */}
    <Box p="$8x" backgroundColor="$backgroundSecondary" accessibilityRole="header" />
    <Box p="$8x" backgroundColor="$backgroundSecondary" accessibilityRole="header">
      <Text tag="span" typography="$labelSmall" fontWeight="$medium" color="$contentTertiary">
        {competitorLabel}
      </Text>
    </Box>
    <Box
      p="$8x"
      borderLeftWidth={3}
      borderLeftColor="$contentAccentBrand"
      backgroundColor="$backgroundSecondary"
      accessibilityRole="header"
    >
      <Text tag="span" typography="$labelSmall" fontWeight="$medium" color="$contentAccentBrand">
        {opendoorLabel}
      </Text>
    </Box>

    {/* Data rows */}
    {rows.map((row) => (
      <React.Fragment key={row.fields.category}>
        <Box
          p="$8x"
          borderTopWidth={1}
          borderTopColor="$borderSecondary"
          accessibilityRole="header"
        >
          <Text tag="span" typography="$labelSmall" fontWeight="$medium" color="$contentSecondary">
            {row.fields.category}
          </Text>
        </Box>
        <Box p="$8x" borderTopWidth={1} borderTopColor="$borderSecondary">
          <Row gap="$4x" alignItems="flex-start">
            <SentimentIcon sentiment={row.fields.competitorSentiment} />
            <Text tag="span" typography="$bodySmall" color="$contentSecondary">
              {row.fields.competitorText}
            </Text>
          </Row>
        </Box>
        <Box
          p="$8x"
          borderLeftWidth={3}
          borderLeftColor="$contentAccentBrand"
          borderTopWidth={1}
          borderTopColor="$borderSecondary"
          backgroundColor="$backgroundSecondary"
        >
          <Row gap="$4x" alignItems="flex-start">
            <SentimentIcon sentiment={row.fields.opendoorSentiment} />
            <Text tag="span" typography="$bodySmall" color="$contentPrimary">
              {row.fields.opendoorText}
            </Text>
          </Row>
        </Box>
      </React.Fragment>
    ))}
  </Box>
);

const RenderCompareTable = (entry: ICompareTable) => {
  const { fields } = entry;

  return (
    <Box backgroundColor="$backgroundSecondary" py="$40x" $smallerThanMD={{ py: '$24x' }}>
      <Box display="block" maxWidth={1120} px="$12x" width="100%" mx="auto">
        <Col gap="$12x">
          <Col gap="$6x">
            <Text tag="p" typography="$labelSmall" fontWeight="$medium" color="$contentAccentBrand">
              {fields.eyebrow}
            </Text>
            <Text
              tag="h2"
              typography="$subheaderLarge"
              color="$contentPrimary"
              $smallerThanMD={{ typography: '$subheaderMedium' }}
            >
              {fields.title}
            </Text>
            <Text tag="p" typography="$bodyLarge" color="$contentSecondary" maxWidth={580}>
              {fields.description}
            </Text>
          </Col>
          <Box display="block" $largerThanSM={{ display: 'none' }}>
            <MobileCards rows={fields.rows} competitorLabel={fields.competitorLabel} />
          </Box>
          <Box display="none" $largerThanSM={{ display: 'block' }}>
            <DesktopGrid
              rows={fields.rows}
              competitorLabel={fields.competitorLabel}
              opendoorLabel={fields.opendoorLabel}
            />
          </Box>
        </Col>
      </Box>
    </Box>
  );
};

const CompareTable: EntryComponent<ICompareTable> = {
  render: RenderCompareTable,
};

export default CompareTable;
